/* eslint-disable max-lines -- don't know how to split this file */
import { inject, Injectable } from '@angular/core';
import { assert, isDefined, isEqualAtDepth, mapToObject } from '@sama/js-core';
import { isEmpty, isEqual, mapValues, partition, pick, sortBy } from 'lodash';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import {
  getOrThrow,
  strictValues,
} from '../../../cross-team-shared/misc-utils/strict-record';
import { Selection } from '../../h3ml/state/fusion-state';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { calculateSafeAverage, getSortedShapes } from '../utils/object-utils';
import { ObjectScopingService } from './object-scoping.service';

@Injectable()
export class ObjectSortingService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private objectScopingService = inject(ObjectScopingService);

  getTopSortIndex(scopedObjectId?: number) {
    const objects =
      this.objectScopingService.getObjectsInSpecifiedScope(scopedObjectId);

    if (!objects.length) {
      return 1;
    }

    const minIndex = Math.min(...objects.map((object) => object.sortIndex));
    const newIndex = Math.floor(minIndex) - 1;
    assert(newIndex < minIndex, 'reached min number!');

    return newIndex;
  }

  getSortedObjects(scopedObjectId: number | undefined) {
    return sortObjects(
      this.objectScopingService.getObjectsInSpecifiedScope(scopedObjectId),
    );
  }

  getShapeOrder$(outputName: string) {
    return combineLatest([
      this.getObjectsChangeTriggers$(outputName),
      this.getShapesChangeTriggers$(outputName),
    ]).pipe(map(() => this.getShapeOrder(outputName)));
  }

  getShapeOrder(outputName: string) {
    const objects = this.taskWorkspaceStore.state().objects;
    const shapes = this.taskWorkspaceStore.getShapesStore(outputName).state();
    const sortedShapes = getSortedShapes(objects, shapes);
    return {
      shapes: mapToObject(sortedShapes, (shape) => [
        shape.index,
        shapes[shape.index],
      ]),
      groups: {},
      shapeAndGroupOrder: sortedShapes,
    };
  }

  /*
   * Updates the sortIndex of specified objects ensuring their order is
   * preserved and their new sortIndex is between the previous and next object's
   * sortIndex. Assumes the previous and next object are in the same scope.
   */
  updateSortIndexes(
    previousObjectId: number | undefined,
    nextObjectId: number | undefined,
    objectIds = this.taskWorkspaceStore.state().objectSelection,
  ) {
    if (isEmpty(objectIds)) {
      return;
    }

    const { objectsToMove, remainingObjects } =
      this.getOrderedObjectsToMoveAndRemainingObjects(objectIds);

    try {
      this.updateSortIndexesBetween(
        previousObjectId,
        nextObjectId,
        // @ts-ignore -- fix me
        objectsToMove,
      );
    } catch (e) {
      if (e instanceof DecimalOverflowError) {
        this.resetIndexes(
          previousObjectId,
          nextObjectId,
          // @ts-ignore -- fix me
          objectsToMove,
          remainingObjects,
        );
        return;
      }
      throw e;
    }
  }

  private getObjectsChangeTriggers$(outputName: string) {
    return this.taskWorkspaceStore('objects').$.pipe(
      distinctUntilChanged((x, y) =>
        isEqual(
          strictValues(x).map((obj) =>
            this.buildComparableObjectForShapeSort(obj, outputName),
          ),
          strictValues(y).map((obj) =>
            this.buildComparableObjectForShapeSort(obj, outputName),
          ),
        ),
      ),
    );
  }

  private getShapesChangeTriggers$(outputName: string) {
    return this.taskWorkspaceStore
      .getShapesStore(outputName)
      .$.pipe(
        distinctUntilChanged((x, y) =>
          isEqualAtDepth(
            2,
            pickRelevantShapeAttributes(x),
            pickRelevantShapeAttributes(y),
          ),
        ),
      );
  }

  private getOrderedObjectsToMoveAndRemainingObjects(objectIds: Selection) {
    const { objects } = this.taskWorkspaceStore.state();
    // @ts-ignore -- fix me
    const scopedObjectId = objects[keys(objectIds)[0]].parentId;

    const [objectsToMove, remainingObjects] = partition(
      this.getSortedObjects(scopedObjectId),
      // @ts-ignore -- fix me
      (object) => objectIds[object.index],
    );
    return { objectsToMove, remainingObjects };
  }

  private updateSortIndexesBetween(
    previousObjectId: number | undefined,
    nextObjectId: number | undefined,
    objectsToMove: ObjectState[],
  ) {
    const newIndexes = this.calculateIndexes(
      previousObjectId,
      objectsToMove,
      nextObjectId,
    );

    this.taskWorkspaceStore.batch(() => {
      objectsToMove.forEach((object, index) => {
        this.taskWorkspaceStore
          .getObjectStore(object.index)('sortIndex')
          .set(newIndexes[index]);
      });
    });
  }

  private calculateIndexes(
    previousObjectId: number | undefined,
    objectsToMove: ObjectState[],
    nextObjectId: number | undefined,
  ) {
    const { objects } = this.taskWorkspaceStore.state();

    // moving everything to the top
    if (previousObjectId === undefined) {
      return this.calculateIndexesAtTop(
        objectsToMove.length,
        // @ts-ignore -- fix me
        objects[nextObjectId!].parentId,
      );
    }

    // moving everything to the bottom
    else if (nextObjectId === undefined) {
      return this.calculateIndexesAtBottom(
        objectsToMove.length,
        // @ts-ignore -- fix me
        objects[previousObjectId].parentId,
      );
    } else {
      return this.calculateIndexesBetween(
        previousObjectId,
        nextObjectId,
        objectsToMove.length,
      );
    }
  }

  private calculateIndexesBetween(
    previousObjectId: number,
    nextObjectId: number,
    requestedIndexCount: number,
  ) {
    const { objects } = this.taskWorkspaceStore.state();

    // @ts-ignore -- fix me
    const previousSortIndex = objects[previousObjectId].sortIndex;
    // @ts-ignore -- fix me
    const nextSortIndex = objects[nextObjectId].sortIndex;
    const result: number[] = [];
    for (let i = 0; i < requestedIndexCount; i++) {
      result[i] = calculateSafeAverage(
        result[i - 1] ?? previousSortIndex,
        nextSortIndex,
      );
    }

    return result;
  }

  private calculateIndexesAtTop(
    requestedIndexCount: number,
    scopedObjectId: number | undefined,
  ) {
    let newIndex = this.getTopSortIndex(scopedObjectId);
    const result = Array(requestedIndexCount)
      .fill(0)
      .map(() => newIndex--);

    // need to reverse to have ascending indexes
    result.reverse();

    // we could have reached min number while decrementing one of the indexes
    assert(
      requestedIndexCount === 1 || result[0] < result[1],
      'reached min number!',
    );
    return result;
  }

  private calculateIndexesAtBottom(
    requestedIndexCount: number,
    scopedObjectId: number | undefined,
  ) {
    let newIndex = this.getBottomSortIndex(scopedObjectId);
    return Array(requestedIndexCount)
      .fill(0)
      .map(() => newIndex++);
  }

  private getBottomSortIndex(scopedObjectId?: number) {
    const objects =
      this.objectScopingService.getObjectsInSpecifiedScope(scopedObjectId);

    if (!objects.length) {
      return 1;
    }

    const maxIndex = Math.max(...objects.map((object) => object.sortIndex));
    return Math.floor(maxIndex) + 1;
  }

  private resetIndexes(
    previousObjectId: number | undefined,
    nextObjectId: number | undefined,
    objectsToMove: ObjectState[],
    remainingObjects: ObjectState[],
  ) {
    let insertIndex: number;
    if (isDefined(previousObjectId)) {
      insertIndex =
        remainingObjects.findIndex(
          (object) => object.index === previousObjectId,
        ) + 1;
    } else {
      insertIndex = remainingObjects.findIndex(
        (object) => object.index === nextObjectId,
      );
    }
    remainingObjects.splice(insertIndex, 0, ...objectsToMove);
    this.taskWorkspaceStore.batch(() => {
      remainingObjects.forEach((object, index) => {
        this.taskWorkspaceStore
          .getObjectStore(object.index)('sortIndex')
          .set(index);
      });
    });
  }

  private buildComparableObjectForShapeSort(
    { sortIndex, childShapeIds }: ObjectState,
    annotationOutputName: string,
  ) {
    const shapes = getOrThrow(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
      annotationOutputName,
    ).shapes;
    return {
      sortIndex,
      childShapeIds: childShapeIds.filter((shapeId) => shapes[shapeId]),
    };
  }
}

export class DecimalOverflowError extends Error {}

export function sortObjects(objects: ObjectState[]) {
  return sortBy(objects, (object) => object.sortIndex);
}

function pickRelevantShapeAttributes(shapes: StrictRecord<number, ShapeState>) {
  return mapValues(shapes, (shape) => pick(shape, 'index', 'key_locations'));
}
